import { Alert, Group, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Content } from '@/core/components';

import { Id } from '@feature/utils';

import { getTravelOwnDayBalanceOptions } from '../model/TravelRequestQuery';
import { TravelDayBalanceExceeded, TravelDayBalanceStat, TravelDayBalanceTrips } from './TravelDayBalanceCommon';

const yearsBack = 4;

interface TravelOwnDayBalancePanelProps {
  year: number;
  onYearChange: (year: number) => void;
  userId?: Id | null;
  title?: string;
  controls?: ReactNode;
}

export function TravelOwnDayBalancePanel({
  year,
  onYearChange,
  userId,
  title,
  controls,
}: TravelOwnDayBalancePanelProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { data, isError, isPending } = useQuery(getTravelOwnDayBalanceOptions(year, userId));
  const years = Array.from({ length: yearsBack + 2 }, (_, index) => String(currentYear + 1 - index));

  return (
    <Content
      bg="white"
      title={title ?? t('travel.requests.dayBalance.title')}
    >
      <Stack>
        <Group align="end">
          <Select
            label={t('travel.requests.dayBalance.year')}
            data={years}
            value={String(year)}
            onChange={value => value != null && onYearChange(Number(value))}
            allowDeselect={false}
            w={120}
          />
          {controls}
        </Group>
        {isError && <Alert color="red">{t('travel.requests.dayBalance.error')}</Alert>}
        {isPending && (
          <Text
            size="sm"
            c="dimmed"
          >
            {t('travel.requests.dayBalance.loading')}
          </Text>
        )}
        {data != null && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TravelDayBalanceStat
                label={t('travel.requests.dayBalance.withPaperUsed', { cap: data.withPaperCap })}
                value={`${data.withPaperUsed} / ${data.withPaperCap}`}
                used={data.withPaperUsed}
                cap={data.withPaperCap}
                hint={
                  <TravelDayBalanceExceeded
                    used={data.withPaperUsed}
                    cap={data.withPaperCap}
                  />
                }
              />
              <TravelDayBalanceStat
                label={t('travel.requests.dayBalance.professionalDevelopmentUsed', {
                  cap: data.professionalDevelopmentCap,
                })}
                value={`${data.professionalDevelopmentUsed} / ${data.professionalDevelopmentCap}`}
                used={data.professionalDevelopmentUsed}
                cap={data.professionalDevelopmentCap}
                hint={
                  <TravelDayBalanceExceeded
                    used={data.professionalDevelopmentUsed}
                    cap={data.professionalDevelopmentCap}
                  />
                }
              />
              <TravelDayBalanceStat
                label={t('travel.requests.dayBalance.remaining')}
                value={Math.max(data.withPaperCap - data.withPaperUsed, 0)}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TravelDayBalanceStat
                label={t('travel.requests.dayBalance.vacationDays')}
                value={data.vacationDays}
              />
              <TravelDayBalanceStat
                label={t('travel.requests.dayBalance.unpaidDays')}
                value={data.unpaidDays}
              />
            </SimpleGrid>
            {data.trips.length === 0 ? (
              <Text
                size="sm"
                c="dimmed"
              >
                {t('travel.requests.dayBalance.empty', { year: data.year })}
              </Text>
            ) : (
              <TravelDayBalanceTrips trips={data.trips} />
            )}
            <Text
              size="xs"
              c="dimmed"
            >
              {t('travel.requests.dayBalance.note')}
            </Text>
          </>
        )}
      </Stack>
    </Content>
  );
}
